import { atom } from "jotai";
import {
    AccountHintType,
    Draft,
    DraftRow,
    DraftSnapshot,
} from "@/entities/DataAssociation/Draft.model";
import { QueryAtom } from "@/api/types/query.types";
import { Task } from "@/entities/DataAssociation/Task.model";
import { accountsAtom } from "./account.state";
import { isReadonlyAtom } from "./params.state";
import { StatementType } from "@/entities/DataAssociation/File.model";
import { BSLabel, ISLabel } from "@/entities/DataAssociation/Account.model";

export const readOnlyDraftAtom = atom<QueryAtom<Draft>>({
    isLoading: false,
    isError: false,
    data: null,
});

export const draftAtom = atom<QueryAtom<Draft>>({
    isLoading: false,
    isError: false,
    data: null,
});

export const tasksAtom = atom<QueryAtom<Task[]>>({
    isLoading: false,
    isError: false,
    data: [],
});

export const draftListAtom = atom<QueryAtom<DraftSnapshot[]>>({
    isLoading: false,
    isError: false,
    data: [],
});

const baseAccountApplicationAtom = atom<Record<string, string>>({});

export const accountApplicationAtom = atom(
    (get) => get(baseAccountApplicationAtom),
    (get, set, value: { rowHeader: string; accountId: string }) => {
        if (value === null) {
            set(baseAccountApplicationAtom, {});
        } else {
            set(baseAccountApplicationAtom, (prev) => ({
                ...prev,
                [value.rowHeader]: value.accountId,
            }));
        }
    },
);

export const updatedDraftAtom = atom((get) => {
    const draft = get(draftAtom);
    const accounts = get(accountsAtom);
    const accountApplication = get(accountApplicationAtom);

    if (!draft.data) return null;
    if (!accountApplication) return draft.data;

    const rows: Record<string, DraftRow> = {};

    Object.entries(draft.data.rows).forEach(([rowId, row]) => {
        if (accountApplication[rowId]) {
            const appliedAccount = accounts[accountApplication[rowId]];
            if (!appliedAccount) {
                rows[rowId] = row;
                return;
            }
            rows[rowId] = {
                ...row,
                account: {
                    type: appliedAccount.type,
                    accountId:
                        appliedAccount.type === AccountHintType.EXISTING
                            ? appliedAccount.id
                            : undefined,
                    label: appliedAccount.label,
                    accountName: appliedAccount.name,
                    meta: row.account?.meta,
                },
            };
        } else {
            rows[rowId] = row;
        }
    });

    return { ...draft.data, rows };
});

export const currentDraftAtom = atom<QueryAtom<Draft>>((get) => {
    const draftPreview = get(readOnlyDraftAtom);
    const draft = get(draftAtom);
    const updatedDraft = get(updatedDraftAtom);
    const isReadonly = get(isReadonlyAtom);

    return isReadonly
        ? {
              ...draftPreview,
              isLoading: !draft && draftPreview.isLoading,
          }
        : {
              isLoading: !draftPreview && draft.isLoading,
              data: updatedDraft || draft.data || null,
              isError: draft.isError,
          };
});

export const draftSubmissionAtom = atom((get) => {
    const draft = get(draftAtom);
    const accounts = get(accountsAtom);
    const accountApplication = get(accountApplicationAtom);

    if (!draft.data) return null;
    if (!accountApplication) return draft.data;

    const rows: Record<string, DraftRow> = {};

    Object.entries(draft.data.rows).forEach(([rowId, row]) => {
        if (accountApplication[rowId]) {
            const appliedAccount = accounts[accountApplication[rowId]];
            if (appliedAccount) {
                rows[rowId] = rows[rowId] = {
                    ...row,
                    account: {
                        type: appliedAccount.type,
                        accountId:
                            appliedAccount.type === AccountHintType.EXISTING
                                ? appliedAccount.id
                                : undefined,
                        label: appliedAccount.label,
                        accountName: appliedAccount.name,
                        meta: row.account?.meta,
                    },
                };
                return;
            }
        }
        rows[rowId] = { ...row, account: null };
    });

    return { ...draft.data, rows };
});

export const applyExistingAccountsAtom = atom(null, (get, set) => {
    const draft = get(draftAtom);
    const accountApplication = get(accountApplicationAtom);

    if (!draft.data?.rows) return;

    Object.entries(draft.data.rows).forEach(([rowId, row]) => {
        if (accountApplication[rowId]) return;
        if (row.account?.type !== AccountHintType.EXISTING) return;
        if (!row.account.accountId) return;

        set(accountApplicationAtom, {
            rowHeader: rowId,
            accountId: row.account.accountId,
        });
    });
});

export const derivedStatementTypesAtom = atom((get) => {
    const currentDraft = get(currentDraftAtom);
    if (!currentDraft.data?.rows) {
        return [];
    }
    const statementTypes = new Set<StatementType>();
    Object.values(currentDraft.data.rows).forEach((row) => {
        if (!row.account?.label) return;
        const label = row.account.label;
        if (Object.values(ISLabel).includes(label as ISLabel)) {
            statementTypes.add(StatementType.INCOME_STATEMENT);
        } else if (Object.values(BSLabel).includes(label as BSLabel)) {
            statementTypes.add(StatementType.BALANCE_SHEET);
        }
    });
    return Array.from(statementTypes);
});
